/**
 * Y-Axis Tick Generation System
 *
 * Product Requirements:
 * - Default: Whole-number K or M ticks (20K, 23M) - no decimals
 * - 4-6 ticks target
 * - Exception: Allow 1 decimal place ONLY when whole numbers would:
 *   1. Exceed data bounds (show 24K when max is 23.02K)
 *   2. Create duplicate labels
 *   3. Result in < 3 ticks
 * - Never show ticks outside data range
 * - Never use raw integers on y-axis (always use K/M/Integer mode)
 * - Never show duplicate labels
 */

type LabelMode = 'integer' | 'K' | 'M';

interface TickResult {
  readonly ticks: readonly number[];
  readonly domain: readonly [number, number];
  readonly tickFormatter: (value: number) => string;
}

const TARGET_TICK_COUNT = 4;
const MIN_TICK_COUNT = 3;
const MAX_TICK_COUNT = 6;

// Mode thresholds
const K_THRESHOLD = 10_000;
const M_THRESHOLD = 1_000_000;

// Maximum acceptable domain expansion beyond data (as ratio)
const MAX_EXPANSION_RATIO = 1.5;

/**
 * Determine label mode based on max value
 */
const determineLabelMode = (max: number): LabelMode => {
  if (max >= M_THRESHOLD) return 'M';
  if (max >= K_THRESHOLD) return 'K';
  return 'integer';
};

/**
 * Get divisor for a mode
 */
const getDivisor = (mode: LabelMode): number => {
  switch (mode) {
    case 'M':
      return 1_000_000;
    case 'K':
      return 1_000;
    case 'integer':
      return 1;
  }
};

/**
 * Format value with specified precision
 * precision 0 = whole numbers (20K)
 * precision 1 = one decimal (20.5K)
 */
const formatValue = (
  value: number,
  mode: LabelMode,
  precision: 0 | 1,
): string => {
  const divisor = getDivisor(mode);
  const divided = value / divisor;

  switch (mode) {
    case 'M':
      return precision === 0
        ? `${Math.round(divided)}M`
        : `${divided.toFixed(1)}M`;
    case 'K':
      return precision === 0
        ? `${Math.round(divided)}K`
        : `${divided.toFixed(1)}K`;
    case 'integer':
      return Math.round(divided).toLocaleString('en-US');
  }
};

/**
 * Get base step sizes for a mode
 * Returns [whole number steps, fractional steps]
 */
const getStepsForMode = (
  mode: LabelMode,
): { wholeSteps: number[]; fractionalSteps: number[] } => {
  switch (mode) {
    case 'M':
      return {
        wholeSteps: [1_000_000, 2_000_000, 5_000_000], // 1M, 2M, 5M
        fractionalSteps: [100_000, 200_000, 500_000], // 0.1M, 0.2M, 0.5M
      };
    case 'K':
      return {
        wholeSteps: [1_000, 2_000, 5_000], // 1K, 2K, 5K
        fractionalSteps: [100, 200, 500], // 0.1K, 0.2K, 0.5K
      };
    case 'integer':
      return {
        wholeSteps: [1, 2, 5, 10, 20, 50, 100, 200, 500],
        fractionalSteps: [],
      };
  }
};

/**
 * Generate step candidates for a given range
 */
const getStepCandidates = (
  range: number,
  mode: LabelMode,
  allowFractional: boolean,
): readonly number[] => {
  const { wholeSteps, fractionalSteps } = getStepsForMode(mode);
  const candidates: number[] = [];

  // Generate candidates by scaling base steps
  const baseSteps = allowFractional
    ? [...wholeSteps, ...fractionalSteps]
    : wholeSteps;

  for (let power = -1; power <= 4; power++) {
    const multiplier = Math.pow(10, power);
    baseSteps.forEach((base) => {
      const step = base * multiplier;
      // Only include steps that make sense for this range
      if (step >= range / 10 && step <= range * 2) {
        candidates.push(step);
      }
    });
  }

  return candidates.sort((a, b) => a - b);
};

/**
 * Round down to nearest step
 */
const floorToStep = (value: number, step: number): number =>
  Math.floor(value / step) * step;

/**
 * Round up to nearest step
 */
const ceilToStep = (value: number, step: number): number =>
  Math.ceil(value / step) * step;

/**
 * Generate ticks for given bounds and step
 */
const generateTicksForStep = (
  min: number,
  max: number,
  step: number,
): readonly number[] => {
  const ticks: number[] = [];
  const epsilon = step * 0.0001;

  for (let tick = min; tick <= max + epsilon; tick += step) {
    // Round based on step size to avoid floating point errors
    const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
    const roundedTick = Number(tick.toFixed(decimals));
    ticks.push(roundedTick);
  }

  return ticks;
};

/**
 * Check if labels are unique
 */
const hasUniqueLabels = (
  ticks: readonly number[],
  formatter: (v: number) => string,
): boolean => {
  const labels = ticks.map(formatter);
  return new Set(labels).size === labels.length;
};

/**
 * Check if configuration is valid
 */
const isValidConfiguration = (
  ticks: readonly number[],
  domainMin: number,
  domainMax: number,
  dataMin: number,
  dataMax: number,
  formatter: (v: number) => string,
): boolean => {
  // Must have valid tick count
  if (ticks.length < MIN_TICK_COUNT || ticks.length > MAX_TICK_COUNT) {
    return false;
  }

  // Labels must be unique
  if (!hasUniqueLabels(ticks, formatter)) {
    return false;
  }

  // Domain must contain data
  if (domainMin > dataMin || domainMax < dataMax) {
    return false;
  }

  // Domain shouldn't expand too far beyond data
  const dataRange = dataMax - dataMin;
  const domainRange = domainMax - domainMin;
  const expansionRatio = domainRange / dataRange;

  if (expansionRatio > MAX_EXPANSION_RATIO) {
    return false;
  }

  return true;
};

/**
 * Score a configuration (lower is better)
 */
const scoreConfiguration = (
  tickCount: number,
  domainRange: number,
  dataRange: number,
): number => {
  const expansionRatio = domainRange / dataRange;
  const expansionPenalty = (expansionRatio - 1) * 10;
  const tickPenalty = Math.abs(tickCount - TARGET_TICK_COUNT);

  return expansionPenalty + tickPenalty;
};

/**
 * Try to find valid configuration with given precision
 */
const tryFindConfiguration = (
  dataMin: number,
  dataMax: number,
  mode: LabelMode,
  precision: 0 | 1,
): TickResult | null => {
  const range = dataMax - dataMin;
  const allowFractional = precision === 1;
  const candidates = getStepCandidates(range, mode, allowFractional);
  const formatter = (v: number) => formatValue(v, mode, precision);

  let bestConfig: TickResult | null = null;
  let bestScore = Infinity;

  for (const step of candidates) {
    const cleanMin = floorToStep(dataMin, step);
    const cleanMax = ceilToStep(dataMax, step);
    const ticks = generateTicksForStep(cleanMin, cleanMax, step);

    // Check if valid
    if (
      !isValidConfiguration(
        ticks,
        cleanMin,
        cleanMax,
        dataMin,
        dataMax,
        formatter,
      )
    ) {
      continue;
    }

    // Score it
    const score = scoreConfiguration(ticks.length, cleanMax - cleanMin, range);

    if (score < bestScore) {
      bestScore = score;
      bestConfig = {
        ticks,
        domain: [cleanMin, cleanMax],
        tickFormatter: formatter,
      };
    }
  }

  return bestConfig;
};

/**
 * Main function: Compute Y-axis ticks
 * Priority: Try whole numbers first, fall back to 1 decimal if needed
 */
export const computeYAxisTicks = (
  dataMin: number,
  dataMax: number,
): TickResult => {
  // Handle invalid input
  if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) {
    return {
      ticks: [0, 50, 100],
      domain: [0, 100],
      tickFormatter: (v) => v.toLocaleString('en-US'),
    };
  }

  // Handle flat line
  if (dataMin === dataMax) {
    const padding = Math.max(dataMin * 0.1, 10);
    return computeYAxisTicks(Math.max(0, dataMin - padding), dataMax + padding);
  }

  // Ensure min < max
  const [min, max] =
    dataMin < dataMax ? [dataMin, dataMax] : [dataMax, dataMin];

  // Determine label mode
  const mode = determineLabelMode(max);

  // Priority 1: Try whole numbers (precision 0)
  const wholeNumberConfig = tryFindConfiguration(min, max, mode, 0);
  if (wholeNumberConfig) {
    return wholeNumberConfig;
  }

  // Priority 2: Fall back to 1 decimal for K/M modes only
  if (mode !== 'integer') {
    const decimalConfig = tryFindConfiguration(min, max, mode, 1);
    if (decimalConfig) {
      return decimalConfig;
    }
  }

  // Fallback: Use basic configuration, ensure we don't exceed MAX_TICK_COUNT
  const range = max - min;
  const divisor = getDivisor(mode);
  let step =
    mode === 'integer'
      ? Math.pow(10, Math.floor(Math.log10(range / 4)))
      : divisor;

  let cleanMin = floorToStep(min, step);
  let cleanMax = ceilToStep(max, step);
  let ticks = generateTicksForStep(cleanMin, cleanMax, step);

  // Cap tick count - increase step until within limit
  while (ticks.length > MAX_TICK_COUNT && step < cleanMax - cleanMin) {
    step = step * 2;
    cleanMin = floorToStep(min, step);
    cleanMax = ceilToStep(max, step);
    ticks = generateTicksForStep(cleanMin, cleanMax, step);
  }

  // If still over limit, subsample evenly
  const finalTicks =
    ticks.length <= MAX_TICK_COUNT
      ? ticks
      : Array.from(
          { length: MAX_TICK_COUNT },
          (_, i) =>
            ticks[Math.round((i / (MAX_TICK_COUNT - 1)) * (ticks.length - 1))],
        );

  const formatter = (v: number) => formatValue(v, mode, 0);

  return {
    ticks: finalTicks,
    domain: [cleanMin, cleanMax],
    tickFormatter: formatter,
  };
};

// Re-export types for external use
export type { TickResult as YAxisTicksResult };
